import React, { useLayoutEffect, useEffect, useState, useRef } from 'react';
import './Home.css';
import CategoryBackground from './CategoryBackground';

const LINE_DELAY = 0.12; // secondes entre chaque ligne
const IDLE_DELAY_MS = 30000; // inactivité avant le diaporama d'attente (30 s)

const SEG1 = "Studio Oblik";
const REG = "®";
// La phrase est coupée après « l’inédit! » : le retour à la ligne est
// imposé quel que soit le format, pour que les deux idées restent nettes.
const SEG2 = " est une agence de conception graphique et de marketing à la recherche de l’inédit!";
const SEG2B = "De Bruxelles à Paris et de Paris à ";
const SEG3 = "ton coeur";

function makeWordSpans(text) {
  const tokens = text.split(/(\s+)/);
  return tokens.map((token, i) => {
    if (token === '' || /^\s+$/.test(token)) {
      return <React.Fragment key={i}>{token}</React.Fragment>;
    }
    return (
      <span key={i} className="type-word">
        {token}
      </span>
    );
  });
}

export default function Home({ onNavClick }) {
  const [hovered, setHovered] = useState(null);
  const [imgPos, setImgPos] = useState({ x: 0, y: 0 });
  const [activeCat, setActiveCat] = useState(null); // filtre survolé
  const [renderCat, setRenderCat] = useState(null); // catégorie affichée (maintenue pendant le fondu de sortie)
  const [idleActive, setIdleActive] = useState(false); // diaporama d'attente visible
  const [idleRender, setIdleRender] = useState(false); // maintenu pendant le fondu de sortie
  const clearTimer = useRef(null);
  const idleTimer = useRef(null);
  const idleFade = useRef(null);
  const idleOn = useRef(false);
  const containerRef = useRef(null);
  const pRef = useRef(null);

  const enterCat = (c) => {
    clearTimeout(clearTimer.current);
    setRenderCat(c);
    setActiveCat(c);
  };
  const leaveCat = () => {
    setActiveCat(null);
    clearTimer.current = setTimeout(() => setRenderCat(null), 550);
  };

  // Diaporama d'attente : après un moment sans activité, les travaux
  // apparaissent en fond (même effet que le survol des catégories).
  // La moindre activité annule et remet le site comme initialement.
  useEffect(() => {
    const arm = () => {
      clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        idleOn.current = true;
        clearTimeout(idleFade.current);
        setIdleRender(true);
        setIdleActive(true);
      }, IDLE_DELAY_MS);
    };
    const onActivity = () => {
      if (idleOn.current) {
        idleOn.current = false;
        setIdleActive(false);
        idleFade.current = setTimeout(() => setIdleRender(false), 550);
      }
      arm();
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart'];
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    arm();
    return () => {
      clearTimeout(idleTimer.current);
      clearTimeout(idleFade.current);
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
    };
  }, []);

  // Bascule les textes / logo / nav en mode "différence" quand un fond est actif
  useEffect(() => {
    const active = !!activeCat || idleActive;
    document.body.classList.toggle('cat-bg-active', active);
    return () => document.body.classList.remove('cat-bg-active');
  }, [activeCat, idleActive]);

  useLayoutEffect(() => {
    const fitText = () => {
      const container = containerRef.current;
      const p = pRef.current;
      if (!container || !p) return;
      // Sur mobile comme sur desktop : le texte est agrandi automatiquement
      // pour remplir toute la zone disponible (aligné en haut)
      const style = getComputedStyle(container);
      const availableHeight = container.clientHeight
        - parseFloat(style.paddingTop)
        - parseFloat(style.paddingBottom)
        - 24; // marge pour les descendantes (g, y, p...)
      let low = 8, high = 300;
      for (let i = 0; i < 25; i++) {
        const mid = (low + high) / 2;
        p.style.fontSize = mid + 'px';
        if (p.scrollHeight <= availableHeight) {
          low = mid;
        } else {
          high = mid;
        }
      }
      // Sur téléphone, on reste légèrement en dessous du maximum possible :
      // le bloc respire un peu au lieu de toucher les bords de sa zone.
      const facteur = window.innerWidth <= 900 ? 0.88 : 1;
      p.style.fontSize = low * facteur + 'px';
    };

    // Regroupe les mots par ligne réelle (après mise en page) et échelonne
    // le délai d'apparition par ligne plutôt que par mot.
    const groupByLine = () => {
      const p = pRef.current;
      if (!p) return;
      const words = Array.from(p.querySelectorAll('.type-word'));
      const lineTops = [];
      words.forEach((el) => {
        const top = Math.round(el.getBoundingClientRect().top);
        let lineIndex = lineTops.indexOf(top);
        if (lineIndex === -1) {
          lineTops.push(top);
          lineIndex = lineTops.length - 1;
        }
        el.style.animationDelay = `${lineIndex * LINE_DELAY}s`;
      });
    };

    const update = () => {
      fitText();
      groupByLine();
    };

    update();
    // La police Sombra se charge après le premier rendu : sans ce recalcul,
    // le texte mesuré avec la police de secours devient trop grand une fois
    // Sombra appliquée et chevauche les liens du bas.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(update);
    }
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);


  const handleMouseMove = (e) => {
    setImgPos({ x: e.clientX, y: e.clientY });
  };

  return (
    <div className="home-container">
      {/* Fond de catégorie animé, derrière tout le reste.
          Sert aussi de diaporama d'attente (tous les travaux) après inactivité. */}
      <div className={`cat-bg-wrap${activeCat || idleActive ? ' visible' : ''}`}>
        {renderCat
          ? <CategoryBackground cat={renderCat} />
          : idleRender && <CategoryBackground cat="tous" />}
      </div>

      <main className="main-text" ref={containerRef}>
        <p ref={pRef}>
          <span
            className="studio-oblik"
            onMouseEnter={() => setHovered('oblik')}
            onMouseLeave={() => setHovered(null)}
            onMouseMove={handleMouseMove}
            style={{ position: 'relative', cursor: 'pointer' }}
          >
            {makeWordSpans(SEG1)}
          </span>
          <sup className="registered">{makeWordSpans(REG)}</sup>
          {makeWordSpans(SEG2)}
          <br />
          {makeWordSpans(SEG2B)}
          <span
            className="coeur-span"
            onMouseEnter={() => setHovered('coeur')}
            onMouseLeave={() => setHovered(null)}
            onMouseMove={handleMouseMove}
            style={{ position: 'relative', cursor: 'pointer' }}
          >
            {makeWordSpans(SEG3)}
          </span>
        </p>
        {hovered === 'oblik' && (
          <img
            src='/hello.gif'
            alt="Aperçu Studio Oblik"
            style={{
              position: 'fixed',
              left: imgPos.x + 20,
              top: imgPos.y + 20,
              width: 220,
              height: 124,
              pointerEvents: 'none',
              zIndex: 9999,
              borderRadius: '0px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
            }}
          />
        )}
        {hovered === 'coeur' && (
          <img
            src='/coeur.gif'
            alt="Aperçu coeur"
            style={{
              position: 'fixed',
              left: imgPos.x + 20,
              top: imgPos.y + 20,
              width: 250,
              height: 183,
              pointerEvents: 'none',
              zIndex: 9999,
              borderRadius: '0px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
            }}
          />
        )}
      </main>
      <div className="footer">
        <a
          href="#strategie"
          onMouseEnter={() => enterCat('strategie')}
          onMouseLeave={leaveCat}
          onClick={(e) => { e.preventDefault(); onNavClick && onNavClick('work'); }}
        >→STRATÉGIE</a>
        <a
          href="#production"
          onMouseEnter={() => enterCat('production')}
          onMouseLeave={leaveCat}
          onClick={(e) => { e.preventDefault(); onNavClick && onNavClick('work'); }}
        >→PRODUCTION</a>
        <a
          href="#conception"
          onMouseEnter={() => enterCat('conception')}
          onMouseLeave={leaveCat}
          onClick={(e) => { e.preventDefault(); onNavClick && onNavClick('work'); }}
        >→CONCEPTION GRAPHIQUE</a>
      </div>
    </div>
  );
}